import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import Pill from 'components/Luxkit/Pill'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getExperienceOfferPageURL } from 'lib/offerPageURL'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import { useAppSelector } from 'hooks/reduxHooks'

const Tile = styled(Clickable)`
  display: grid;
  grid-template-columns: ${rem(96)} 1fr;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  background-color: ${props => props.theme.palette.neutral.eight};
  border-radius: ${props => props.theme.borderRadius.small};
  overflow: hidden;
  min-height: ${rem(92)};
  box-shadow: none;
  transition: outline 0.2s, box-shadow 0.2s;

  ${mediaQueryUp.sm} {
    min-height: ${rem(96)};
  }

  &:hover {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  .input-keyboard &:focus {
    outline: 2px solid  ${props => props.theme.palette.neutral.five};
    outline-offset: 2px;
  }
`

const Content = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(12)} ${rem(16)};
`

interface Props {
  experience: App.ExperienceItem;
}

function ExperienceRecentlyViewedTile(props: Props) {
  const { experience } = props
  const mainImage = experience.images[0]

  const tripId = useAppSelector(getImmersiveTripId)

  const linkParams = useMemo(() => {
    const queryParams = (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) ?
      new URLSearchParams({ tripId }) :
      undefined
    const to = getExperienceOfferPageURL(experience, queryParams)
    return {
      to,
      ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank' } : {}),
    }
  }, [tripId, experience])

  return <Tile {...linkParams}>
    {mainImage && <Image
      image={mainImage}
      height={96}
      width={96}
      fit="center"
    />}
    <Content gap={8}>
      <BodyText variant="small" weight="bold" lineClamp={2}>{experience.title}</BodyText>
      <div>
        {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <div>
          <Pill kind="primary" variant="success">
            Save up to {experience.discount}%
          </Pill>
        </div>}
        <Caption as="span" variant="large" colour="neutral-three">From</Caption>{' '}
        <Heading as="span" variant="heading6"><FormatCurrency value={experience.price} /></Heading>
        {experience.ticketUnitLabel && <Caption as="span" variant="large" colour="neutral-three">/{experience.ticketUnitLabel}</Caption>}
      </div>
    </Content>
  </Tile>
}

export default ExperienceRecentlyViewedTile
